/**
 * Documentation written by: ChatGPT - Henrik
 * This file initializes the i18n library for internationalization (i18n) in a React application.
 * It configures `i18next` with `react-i18next` for seamless integration and sets up a backend plugin
 * to load translation files dynamically based on the selected language.
 *
 * Key Configurations:
 * - `HttpBackend`: Enables dynamic loading of translation files from the specified path.
 * - `initReactI18next`: Integrates `i18next` with React components.
 * - `debug`: Enables debug mode for logging and troubleshooting during development.
 * - `fallbackLng`: Specifies the default language to use if a translation is unavailable.
 * - `lng`: Sets the initial language to `en` (English).
 * - `backend.loadPath`: Path to load translation files. Uses `{{lng}}` to dynamically determine the language.
 * - `interpolation.escapeValue`: Disabled because React already handles escaping to prevent XSS attacks.
 *
 * Example Usage:
 * - Import and use the configured `i18n` instance in your React components.
 * - Translation files should be organized in the specified `locales/{{lng}}/translation.json` format.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

i18n
	.use(Backend) // Loads translations from your backend (e.g., JSON files)
	.use(LanguageDetector) // Detects the user's language
	.use(initReactI18next) // Passes i18n instance to react-i18next
	.init({
		fallbackLng: "en",
		debug: true,
		interpolation: {
			escapeValue: false, // React already escapes by default
		},
		backend: {
			loadPath: "/locales/{{lng}}/translation.json", // Path to translation files
		},
	});

// Expose a toggle in the browser console for debugging translation keys.
// Usage: debug_translations()  → toggles between normal translations and raw keys.
const _debugTranslations = () => {
	if (i18n.language === "cimode") {
		const prev = sessionStorage.getItem("debug_translations_prev_lng") ?? "nb-NO";
		i18n.changeLanguage(prev);
		console.info(`[i18n] Translations re-enabled (${prev})`);
	} else {
		sessionStorage.setItem("debug_translations_prev_lng", i18n.language);
		i18n.changeLanguage("cimode");
		console.info("[i18n] Translations disabled — raw keys are now shown in the UI.");
	}
};

(window as unknown as { debug_translations: typeof _debugTranslations }).debug_translations =
	_debugTranslations;

export default i18n;
